// Breakdown structures (V12-V16, E01): WBS, PBS, OBS, DBS, CBS, SBS/XBS as code trees.
import { useMutation } from "convex/react";
import { useMemo, useState } from "react";
import { ArrowDown, ArrowUp, Plus, Trash2 } from "lucide-react";
import { api } from "../../../convex/_generated/api";
import type { Doc } from "../../../convex/_generated/dataModel";
import { BD_LABEL, useProject } from "@/hooks/project";
import { Button, Card, Input, PageHeader, Select, Tabs, useRun, useToast } from "@/components/ui";
import { DataGrid, type Col } from "@/components/DataGrid";
import { ExportButton } from "@/components/Importer";
import { exportBreakdown } from "@/lib/formats";
import { MappedImportButton } from "@/components/ColumnMapper";
import { breakdownFields } from "@/lib/importSchemas";
import { num, str } from "@/lib/mapping";
import type { Breakdown } from "@/engine/types";

const HELP: Record<Breakdown, string> = {
  wbs: "Work breakdown: units / areas. Unit codes usually mirror the WBS.",
  pbs: "Phase breakdown (e.g. E, P, C). Each milestone can carry its own PBS.",
  obs: "Organisation: team or office.",
  dbs: "Disciplines. Actual hours are entered at this level. Type Discipline or Procurement.",
  cbs: "Cost breakdown.",
  sbs: "Free field to tie deliverables to P6 (SBS/XBS).",
};

export function BreakdownsPage() {
  const { project, nodes, activeBreakdowns, canEdit } = useProject();
  // Opens on WBS (Cesar, Oct 16 beta session).
  const [type, setType] = useState<Breakdown>(activeBreakdowns.includes("wbs") ? "wbs" : activeBreakdowns[0] ?? "dbs");
  const upsert = useMutation(api.setup.upsertNode);
  const del = useMutation(api.setup.deleteNode);
  const importNodes = useMutation(api.setup.importNodes);
  const reorder = useMutation(api.setup.reorderNodes);
  const run = useRun();
  const toast = useToast();
  const rows = nodes[type];
  const [add, setAdd] = useState({ code: "", title: "", parentCode: "" });
  const [sel, setSel] = useState<Doc<"breakdownNodes">[]>([]);
  const depth = useMemo(() => {
    const byCode = new Map(rows.map((n) => [n.code, n]));
    const d = (n: Doc<"breakdownNodes">, k = 0): number => (n.parentCode && byCode.get(n.parentCode) && k < 20 ? d(byCode.get(n.parentCode)!, k + 1) + 1 : 0);
    return new Map(rows.map((n) => [n.code, d(n)]));
  }, [rows]);
  const save = (n: Doc<"breakdownNodes">, patch: Partial<Doc<"breakdownNodes">>) =>
    run(() => upsert({ projectId: project._id, type, node: { code: n.code, title: n.title, parentCode: n.parentCode, dbsType: n.dbsType, order: n.order, ...patch } }));
  // Move the selected nodes up or down among their siblings; reports follow this order (Cesar, Nov 1: "E, P, C, CM").
  const move = (dir: -1 | 1) => {
    const picked = new Set(sel.map((n) => n.code));
    const parents = new Set(sel.map((n) => n.parentCode ?? ""));
    if (parents.size !== 1) return toast({ text: "Select rows under the same parent to move them", tone: "error" });
    const p = [...parents][0];
    const sibs = rows.filter((n) => (n.parentCode ?? "") === p).map((n) => n.code);
    const order = dir < 0 ? sibs : [...sibs].reverse();
    for (let i = 1; i < order.length; i++) if (picked.has(order[i]) && !picked.has(order[i - 1])) [order[i - 1], order[i]] = [order[i], order[i - 1]];
    return run(() => reorder({ projectId: project._id, type, codes: dir < 0 ? order : order.reverse() }));
  };
  const cols: Col<Doc<"breakdownNodes">>[] = [
    { id: "code", title: "Code", width: 140, get: (r) => `${"  ".repeat(depth.get(r.code) ?? 0)}${r.code}` },
    { id: "title", title: "Title", width: 320, get: (r) => r.title, set: canEdit ? (r, v) => void save(r, { title: String(v ?? "") }) : undefined },
    { id: "parent", title: "Parent", width: 120, options: rows.map((r) => r.code), get: (r) => r.parentCode, set: canEdit ? (r, v) => void save(r, { parentCode: v === null ? undefined : String(v) }) : undefined },
    {
      id: "order",
      title: "Order",
      width: 80,
      kind: "number",
      get: (r) => r.order,
      set: canEdit ? (r, v) => void (typeof v === "number" && v > 0 && save(r, { order: v })) : undefined,
    },
    ...(type === "dbs"
      ? [{ id: "type", title: "Type", width: 120, options: ["discipline", "procurement"], get: (r: Doc<"breakdownNodes">) => r.dbsType, set: canEdit ? (r: Doc<"breakdownNodes">, v: unknown) => void save(r, { dbsType: v as "discipline" }) : undefined } as Col<Doc<"breakdownNodes">>]
      : []),
  ];
  return (
    <div className="max-w-5xl space-y-4">
      <PageHeader title="Breakdown structures" sub="Every code picker in the project is tied to these tables." />
      <Tabs value={type} onChange={setType} items={activeBreakdowns.map((t) => ({ value: t, label: `${BD_LABEL[t]} (${nodes[t].length})` }))} />
      <Card
        title={BD_LABEL[type]}
        actions={
          <>
            <ExportButton file={`${type}.xlsx`} sheets={() => [exportBreakdown(type, rows)]} />
            {canEdit && (
              <MappedImportButton
                fields={breakdownFields(type)}
                rememberKey={`breakdown-${type}`}
                build={(mapped) => {
                  const items = mapped.map((m) => ({
                    line: m.line,
                    code: str(m.values.code),
                    title: str(m.values.title),
                    parentCode: str(m.values.parentCode) || undefined,
                    ...(type === "dbs" ? { dbsType: str(m.values.dbsType).toUpperCase().startsWith("PROC") ? ("procurement" as const) : ("discipline" as const) } : {}),
                    order: num(m.values.order) ?? undefined,
                  }));
                  const ex = new Map(rows.map((n) => [n.code, n]));
                  const count = new Map<string, number>();
                  for (const n of items) count.set(n.code, (count.get(n.code) ?? 0) + 1);
                  return {
                    problems: [],
                    rows: items.map((n) => {
                      const cur = ex.get(n.code);
                      const same = cur && cur.title === n.title && (cur.parentCode ?? "") === (n.parentCode ?? "") && (type !== "dbs" || cur.dbsType === n.dbsType) && (n.order === undefined || cur.order === n.order);
                      const err = !n.code ? "No code" : (count.get(n.code) ?? 0) > 1 ? `Code ${n.code} appears ${count.get(n.code)} times in the file` : "";
                      return { line: n.line, key: n.code || "(blank)", status: err ? "error" : !cur ? "new" : same ? "unchanged" : "changed", detail: err || `${n.title}${n.parentCode ? ` (under ${n.parentCode})` : ""}` };
                    }),
                    commit: () => importNodes({ projectId: project._id, type, nodes: items.map(({ line, ...n }) => (void line, n)) }),
                  };
                }}
              />
            )}
            {canEdit && (
              <>
                <Button variant="secondary" disabled={!sel.length} onClick={() => move(-1)} title="Move the selected rows up among their siblings">
                  <ArrowUp className="size-4" /> Move up
                </Button>
                <Button variant="secondary" disabled={!sel.length} onClick={() => move(1)} title="Move the selected rows down among their siblings">
                  <ArrowDown className="size-4" /> Move down
                </Button>
              </>
            )}
            {canEdit && (
              <Button variant="danger" disabled={!sel.length} onClick={async () => { for (const n of sel) await run(() => del({ id: n._id })); }}>
                <Trash2 className="size-4" /> Delete selected
              </Button>
            )}
          </>
        }
      >
        <p className="mb-3 text-xs text-slate-500">
          {HELP[type]} Reports list the codes in the order shown here: set it with Move up / Move down or the Order column (rows without an order follow, by code).
        </p>
        {canEdit && (
          <form
            className="mb-3 flex flex-wrap gap-2"
            onSubmit={async (e) => {
              e.preventDefault();
              if (!add.code) return toast({ text: "Enter a code", tone: "error" });
              const ok = await run(() => upsert({ projectId: project._id, type, node: { code: add.code, title: add.title, parentCode: add.parentCode || undefined } }));
              if (ok !== undefined) setAdd({ code: "", title: "", parentCode: add.parentCode });
            }}
          >
            <Input placeholder="Code" aria-label="New code" value={add.code} onChange={(e) => setAdd({ ...add, code: e.target.value })} className="w-36" />
            <Input placeholder="Title" aria-label="New title" value={add.title} onChange={(e) => setAdd({ ...add, title: e.target.value })} className="w-72" />
            <Select value={add.parentCode} aria-label="New parent" onChange={(e) => setAdd({ ...add, parentCode: e.target.value })} className="w-44">
              <option value="">No parent (top of the tree)</option>
              {rows.map((r) => (
                <option key={r.code}>{r.code}</option>
              ))}
            </Select>
            <Button type="submit">
              <Plus className="size-4" /> Add
            </Button>
          </form>
        )}
        <DataGrid rows={rows} cols={cols} height={460} onSelectRows={setSel} onEdit={(edits) => edits.forEach((e) => e.col.set?.(e.row, e.value))} onError={(m) => toast({ text: m, tone: "error" })} />
      </Card>
    </div>
  );
}
